import { HttpError } from '@/models/http-error';
import { jsonStreamReader } from '@/resources/http/json-stream-reader';
import { EMPTY, from, of, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { unauthorizedInterceptor } from '../auth/auth';

async function fetchWrapper(route: RequestInfo, options?: RequestInit) {
  const response = await fetch(route, options);
  unauthorizedInterceptor(response);
  if (!response.ok) {
    throw new HttpError(response.status, response.statusText);
  }
  return response;
}

function jsonHandler<T>(response: Response): Observable<T> {
  if (response.status === 204) {
    return EMPTY;
  }
  return from(response.json()).pipe(
    mergeMap(value => {
      if (Array.isArray(value)) {
        return from<T[]>(value);
      }
      return of<T>(value);
    })
  );
}

function jsonStreamHandler<T>(response: Response): Observable<T> {
  if (response.body == null) {
    return EMPTY;
  }
  return jsonStreamReader<T>(response.body.getReader());
}

function fetchObservable<T>(route: RequestInfo, options?: RequestInit): Observable<T> {
  return from(fetchWrapper(route, options)).pipe(
    mergeMap(response => {
      const contentType = response.headers.get('Content-Type') ?? '';
      const isStream = contentType.includes('application/x-ndjson');
      return isStream ? jsonStreamHandler<T>(response) : jsonHandler<T>(response);
    })
  );
}

export default {
  get<T>(route: RequestInfo, options?: RequestInit): Observable<T> {
    const defaultGetOptions = { method: 'GET' };
    const getOptions = Object.assign(defaultGetOptions, options);
    return fetchObservable(route, getOptions);
  },

  post<T>(route: RequestInfo, options?: RequestInit): Observable<T> {
    const defaultPostOptions = { method: 'POST' };
    const postOptions = Object.assign(defaultPostOptions, options);
    return fetchObservable(route, postOptions);
  },

  put<T>(route: RequestInfo, options?: RequestInit): Observable<T> {
    const defaultPutOptions = { method: 'PUT' };
    const putOptions = Object.assign(defaultPutOptions, options);
    return fetchObservable(route, putOptions);
  },

  patch<T>(route: RequestInfo, options?: RequestInit): Observable<T> {
    const defaultPatchOptions = { method: 'PATCH' };
    const patchOptions = Object.assign(defaultPatchOptions, options);
    return fetchObservable(route, patchOptions);
  },

  delete<T>(route: RequestInfo, options?: RequestInit): Observable<T> {
    const defaultDeleteOptions = { method: 'DELETE' };
    const deleteOptions = Object.assign(defaultDeleteOptions, options);
    return fetchObservable(route, deleteOptions);
  }
};
